// WHAT: Grid component for visualizing roadmap items in a timeline/Gantt chart layout
// WHY:  Provides visual timeline representation with zoom levels and status-based styling
// DOMAIN: Admin → Roadmap → Timeline
// DEPENDENCIES: Card, Badge components, Roadmap_Item type
// RISKS:
//   - NOTE: Date calculations may have timezone issues
//   - NOTE: Grid layout uses CSS Grid - may not work on very old browsers
//   - NOTE: Status mapping assumes specific status values - unknown statuses get default styling

import React from "react";

import { Card, Badge } from "@/components/ui";

import { type Roadmap_Item } from "../../../types/roadmap-types";

/**
 * Status styles interface
 */
interface StatusStyles {
  accent: string;
  bg: string;
  border: string;
  text: string;
}

/**
 * Gets status-based styling for timeline items.
 *
 * @param status - Item status string
 *
 * @returns Status styles object
 *
 */
const getStatusStyles = (status: string): StatusStyles => {
  switch (status) {
    case "done": {
      return {
        accent: "bg-green-500",
        bg: "bg-green-500/20",
        border: "border-green-500/50",
        text: "text-green-400",
      };
    }
    case "in_progress": {
      return {
        accent: "bg-tracq-bright-blue",
        bg: "bg-tracq-bright-blue/20",
        border: "border-tracq-bright-blue/50",
        text: "text-tracq-bright-blue",
      };
    }
    case "blocked": {
      return {
        accent: "bg-red-500",
        bg: "bg-red-500/20",
        border: "border-red-500/50",
        text: "text-red-400",
      };
    }
    default: {
      return {
        accent: "bg-slate-500",
        bg: "bg-slate-500/20",
        border: "border-slate-500/50",
        text: "text-slate-300",
      };
    }
  }
};

/**
 * Gets priority-based border color class.
 *
 * @param priority - Item priority string
 *
 * @returns Border color class string
 *
 */
const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case "high": {
      return "border-l-4 border-tracq-deep-orange";
    }
    case "medium": {
      return "border-l-4 border-yellow-500";
    }
    case "low": {
      return "border-l-4 border-green-500";
    }
    default: {
      return "border-l-4 border-slate-500";
    }
  }
};

/**
 * Timeline item interface for grid visualization
 */
interface TimelineItem {
  /** Dependency IDs */
  dependencies: string[];
  /** Effort estimate */
  effort: string;
  /** End date for timeline */
  endDate: Date;
  /** Unique identifier */
  id: string;
  /** Impact score */
  impact: string;
  /** Module name */
  module: string;
  /** Original roadmap item for click handler */
  originalItem?: Roadmap_Item;
  /** Priority level */
  priority: string;
  /** Progress percentage (0-100) */
  progress: number;
  /** Calculated ROI value */
  roi: number;
  /** Start date for timeline */
  startDate: Date;
  /** Status string */
  status: string;
  /** Item title */
  title: string;
}

/**
 * Props for TimelineGrid component
 */
interface TimelineGridProperties {
  /** Currently displayed month */
  currentMonth: Date;
  /** Callback when item is clicked */
  onItemClick: (item: Roadmap_Item) => void;
  /** Array of timeline items to display */
  timelineData: TimelineItem[];
  /** Zoom level (1=month, 2=week, 3=day) */
  zoomLevel: number;
}

/**
 * WHAT:
 *   Component that renders a Gantt chart-style timeline grid showing roadmap items
 *   positioned by their start and end dates with status-based styling.
 *
 * WHY:
 *   Provides visual timeline representation of roadmap items, making it easy to see
 *   item durations, overlaps, and status at a glance.
 *
 * PROPS:
 *   - timelineData: TimelineItem[] — Items to display in timeline
 *   - currentMonth: Date — Currently displayed month
 *   - zoomLevel: number — Zoom level (1=month/4 weeks, 2=week/7 days, 3=day/30 days)
 *   - onItemClick: (item: Roadmap_Item) => void — Handler for item clicks
 *
 * BEHAVIOR:
 *   - Generates grid columns based on zoom level
 *   - Calculates item positions based on start/end dates
 *   - Applies status-based styling (done, in_progress, planned, blocked)
 *   - Applies priority-based border colors
 *   - Displays progress bars within items
 *   - Shows legend for status colors
 *
 * RISKS:
 *   - NOTE: Date calculations use local timezone - may cause issues with UTC dates
 *   - NOTE: Grid layout assumes specific zoom level values - may break if changed
 *   - NOTE: Status mapping doesn't handle all possible status values
 *   - NOTE: Item positioning may overflow grid if dates are outside current month
 *
 * @param root0
 *
 * @param root0.timelineData
 *
 * @param root0.currentMonth
 *
 * @param root0.zoomLevel
 *
 * @param root0.onItemClick
 *
 * @returns Timeline grid component with Gantt chart visualization
 *
 */
export const TimelineGrid: React.FC<TimelineGridProperties> = ({
  currentMonth,
  onItemClick,
  timelineData,
  zoomLevel,
}) => {
  /**
   * WHAT:
   *   Generates timeline grid structure with header and item positions.
   *
   * WHY:
   *   Centralizes grid generation logic based on zoom level.
   *
   * BEHAVIOR:
   *   - Calculates number of columns based on zoom level
   *   - Generates header labels (weeks or days)
   *   - Calculates item positions and durations
   *   - Applies status and priority styling
   *
   * @returns Object with headerItems and timelineItems arrays
   *
   */
  const generateTimelineGrid = () => {
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);

    // Calculate grid columns based on zoom level
    let columns = 30; // default to days
    if (zoomLevel === 1) {
      columns = 4; // weeks
    } else if (zoomLevel === 2) {
      columns = 7; // days of week
    }

    // Generate grid header
    const headerItems = [];
    for (let index = 0; index < columns; index++) {
      const date = new Date(startOfMonth);
      if (zoomLevel === 1) {
        date.setDate(1 + index * 7);
        headerItems.push(
          <div
            className="text-center text-sm text-slate-300 py-2 border-b border-slate-600/50"
            key={index}
          >
            Week {index + 1}
          </div>,
        );
      } else {
        date.setDate(index + 1);
        headerItems.push(
          <div
            className="text-center text-sm text-slate-300 py-2 border-b border-slate-600/50"
            key={index}
          >
            {date.getDate()}
          </div>,
        );
      }
    }

    // Generate timeline items
    const timelineItems = timelineData.map((item, index) => {
      const startDay = Math.max(
        0,
        Math.floor((item.startDate.getTime() - startOfMonth.getTime()) / (1000 * 60 * 60 * 24)),
      );
      const endDay = Math.min(
        columns - 1,
        Math.floor((item.endDate.getTime() - startOfMonth.getTime()) / (1000 * 60 * 60 * 24)),
      );
      const duration = Math.max(1, endDay - startDay + 1);

      const statusStyles = getStatusStyles(item.status);

      return (
        <div
          className={`timeline-item ${statusStyles.bg} ${statusStyles.border} ${getPriorityColor(item.priority)} border rounded-lg p-2 text-white text-sm cursor-pointer hover:border-opacity-100 hover:shadow-tracq-card transition-all duration-200`}
          key={item.id}
          onClick={() => {
            if (item.originalItem) {
              onItemClick(item.originalItem);
            }
          }}
          style={{
            gridColumn: `${String(startDay + 1)} / span ${String(duration)}`,
            gridRow: String(index + 2),
          }}
        >
          <div className="font-medium truncate">{item.title}</div>
          <div className={`text-xs ${statusStyles.text} opacity-90`}>
            {Math.round(item.progress)}% • {item.module}
          </div>
          {item.progress > 0 && (
            <div className="w-full bg-slate-800/50 rounded-full h-1 mt-1">
              <div
                className={`${statusStyles.accent} h-1 rounded-full transition-all duration-300`}
                style={{ width: `${String(item.progress)}%` }}
              />
            </div>
          )}
        </div>
      );
    });

    return { headerItems, timelineItems };
  };

  const { headerItems, timelineItems } = generateTimelineGrid();

  return (
    <Card padding="lg" variant="default">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Timeline View</h3>

        {/* Timeline Grid */}
        <div
          className="grid gap-1"
          style={{
            gridTemplateColumns: (() => {
              if (zoomLevel === 1) {
                return "repeat(4, 1fr)";
              }
              if (zoomLevel === 2) {
                return "repeat(7, 1fr)";
              }
              return "repeat(30, 1fr)";
            })(),
            gridTemplateRows: `auto repeat(${String(timelineData.length)}, 60px)`,
          }}
        >
          {/* Header */}
          {headerItems}

          {/* Timeline Items */}
          {timelineItems}
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap gap-4">
          <Badge size="sm" style="subtle" variant="success">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span>Done</span>
            </div>
          </Badge>
          <Badge size="sm" style="subtle" variant="primary">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-tracq-bright-blue rounded-full" />
              <span>In Progress</span>
            </div>
          </Badge>
          <Badge size="sm" style="subtle" variant="default">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-slate-500 rounded-full" />
              <span>Planned</span>
            </div>
          </Badge>
          <Badge size="sm" style="subtle" variant="destructive">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span>Blocked</span>
            </div>
          </Badge>
        </div>
      </div>
    </Card>
  );
};
